import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Pencil, Coins, IndianRupee } from "lucide-react";

export default function EditBatchModal({ batch, onClose, onSaved }) {
  const [rewardPoints, setRewardPoints] = useState(batch.reward_points ?? 0);
  const [rewardRs, setRewardRs] = useState(batch.reward_value_rs ?? 0);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (Number(rewardPoints) < 0 || Number(rewardRs) < 0) { toast.error("Values cannot be negative"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/coupon-batches/${batch.id}`, {
        reward_points: Number(rewardPoints), reward_value_rs: Number(rewardRs),
      });
      toast.success(`Updated — ${data.coupons_updated} unscanned QR codes refreshed`);
      onSaved(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="edit-batch-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#FDF2F2] flex items-center justify-center text-[#8B0000]"><Pencil className="h-4 w-4" /></div>
            <h3 className="font-display font-bold text-gray-900">Edit Batch Reward</h3>
          </div>
          <button data-testid="edit-batch-close" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-600"><span className="font-semibold text-gray-900">{batch.name}</span> · <span className="capitalize">{batch.target_role || "painter"}</span></div>
          <div>
            <label className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Coins className="h-4 w-4 text-[#8B0000]" /> Reward points per QR</label>
            <input data-testid="edit-reward-points" type="number" min="0" value={rewardPoints} onChange={(e) => setRewardPoints(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-bold outline-none focus:border-[#8B0000]" />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 flex items-center gap-2"><IndianRupee className="h-4 w-4 text-[#8B0000]" /> Reward value per QR (₹)</label>
            <input data-testid="edit-reward-rs" type="number" min="0" step="0.01" value={rewardRs} onChange={(e) => setRewardRs(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-bold outline-none focus:border-[#8B0000]" />
          </div>
          <p className="text-xs text-gray-400">Changes apply only to QR codes not yet scanned. Already-redeemed codes keep their original reward.</p>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-semibold">Cancel</button>
          <button data-testid="edit-batch-save-btn" onClick={save} disabled={busy}
            className="flex items-center gap-2 bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
